import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseInfiniteQueryOptions, useInfiniteQuery } from 'react-query'
import { AlmanacFileSource, BrandKitSource, PlainText, SourceMaterialsMediaObject, Website } from 'types'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { Response } from 'request-dot-js'
import QueryString from 'qs'
import { getNextPageParam } from 'utils/getNextPageParam'

interface Data<T extends SourceType = SourceType> {
  brand_kit_sources: BrandKitSource<SourceTypeMap[T]>[]
}

export type SourceType = 'Website' | 'PlainText' | 'AlmanacFile' | 'SourceMaterialsMediaObject' | 'ExternalIntegration'

interface Props<T extends SourceType> {
  brandKitId: number
  sourceType?: Array<T>
}

type SourceTypeMap = {
  Website: Website
  PlainText: PlainText
  AlmanacFile: AlmanacFileSource
  SourceMaterialsMediaObject: SourceMaterialsMediaObject
  ExternalIntegration: ExternalIntegration
}

export const fetchBrandKitSources = async <T extends SourceType>({
  workspaceId,
  brandKitId,
  sourceType,
  page,
}: {
  workspaceId: number
  brandKitId: number
  page: number
  sourceType?: Array<T>
}) => {
  const queryString = QueryString.stringify({ source_type: sourceType, page }, { arrayFormat: 'comma' })
  const res = await sendToApi<Data<T>>(`/w/${workspaceId}/brand_kits/${brandKitId}/sources?${queryString}`)
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res
}

export const useBrandKitSources = <T extends SourceType = SourceType>(
  { brandKitId, sourceType }: Props<T>,
  options?: UseInfiniteQueryOptions<Response<Data<T>>>,
) => {
  const workspaceId = useCurrentWorkspaceId()

  return useInfiniteQuery<Response<Data<T>>>(
    ['workspaces', workspaceId, 'brandKits', 'detail', brandKitId, 'sources', { sourceType }],
    ({ pageParam = 1 }) => {
      return fetchBrandKitSources<T>({ workspaceId, brandKitId, sourceType, page: pageParam })
    },
    {
      refetchOnWindowFocus: false,
      staleTime: (options?.cacheTime || 0) * 0.5,
      getNextPageParam,
      ...options,
    },
  )
}
